/* @flow */

import * as React from 'react';
import invariant from 'invariant';

import CurrentUser from 'auth/CurrentUser';
import ToastActions from 'actions/ToastActions';
import GroupSchoolSelect from 'groups/components/GroupSchoolSelect.jsx';
import GroupStore from 'groups/stores/GroupStore';
import DirectAddLegalDialog from 'groups/components/DirectAddLegalDialog.jsx';
import { directAddIndividualSubmit } from 'lib/directAddHelper';
import PhoneCallModal from 'phoneCalls/components/PhoneCallModal.jsx';
import { Roles } from 'user/UserConstants';

import Dialog from 'components/Dialog.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import Text from 'components/Text.jsx';
import Link from 'components/Link.jsx';
import BoxBody from 'components/BoxBody.jsx';
import BoxHeader from 'components/BoxHeader.jsx';
import Form from 'components/Form.jsx';
import Button from 'components/Button.jsx';
import FormInput from 'components/FormInput.jsx';
import FormSelect from 'components/FormSelect.jsx';
import { mountModal } from 'lib/modal.js';
import { formatAsPhoneNumber } from 'lib/phoneNumbers';

import './styles/DialerDirectAddModal.scss';

type Props = {
  name?: string,
  number?: string,
  onRequestHide?: Function,
  hideParent: Function,
};

type State = {
  orgId: Backbone$Id,
  role: string,
};

const options = [
  {
    label: 'Parent',
    value: Roles.parent,
  },
  {
    label: 'Student',
    value: Roles.student,
  },
  {
    label: 'Teacher',
    value: Roles.teacher,
  },
];

class DialerDirectAddModal extends React.Component<Props, State> {
  state: State = {
    orgId: CurrentUser.getUser().getOrganizationId(),
    role: '',
  };

  changeSchool = (orgId: Backbone$Id) => {
    this.setState({ orgId });
  };

  startCall = (calleeUuid: string) => {
    mountModal(<PhoneCallModal calleeUuid={calleeUuid} />, this);
  };

  addNewUser = (user: Object) => {
    const group = GroupStore.getStaffClassForOrg(parseInt(this.state.orgId, 10));
    invariant(group, 'must have filtered out groups that does not have staff class');
    const afterFail = err => {
      const errMsg = err.field_errors[0].messages[0];
      ToastActions.showErrorToast(errMsg);
    };

    const afterAdd = (response: Object) => {
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
      this.props.hideParent();
      ToastActions.showSuccessToast(`Added ${user.name}`);
      const uuid = response.invited_user.uuid;
      this.startCall(uuid);
    };

    // TODO -- have this hit the new direct add endpoint via a mutation
    // Once that change is made, we can delete the directAddHelper file
    directAddIndividualSubmit(group, user, afterAdd, afterFail);
  };

  submitForm = (value: Object) => {
    const user = {
      name: value.name,
      address: 'sms://' + value.phoneNumber,
      isOver13: true,
      role: this.state.role,
    };
    this.addNewUser(user);
  };

  changeRole = (newRole: string) => {
    this.setState({ role: newRole });
  };

  render() {
    const displayName =
      this.props.name || (this.props.number && formatAsPhoneNumber(this.props.number));
    invariant(displayName, 'must have one of name or number to start');

    return (
      <Dialog
        hideOnBlur={false}
        hideCloseButton
        size="small"
        className="DialerDirectAdd"
        onRequestHide={this.props.onRequestHide}>
        <BoxHeader
          title="Add to Remind"
          subtext={`To call ${displayName}, add them to your school first.`}
        />
        <BoxBody>
          <Form onValidSubmit={this.submitForm}>
            <FormInput
              label="Name"
              disabled={!!this.props.name}
              required
              autofocus
              ref="name"
              name="name"
              value={this.props.name}
              className="DialerDirectAdd-fullname"
            />
            <FormInput
              label="Phone number"
              prepend="@"
              required
              disabled={!!this.props.number}
              name="phoneNumber"
              ref="code"
              value={this.props.number && formatAsPhoneNumber(this.props.number)}
              validations={{ couldPlausiblyBeANorthAmericanPhoneNumber: true }}
              validationErrors={{
                couldPlausiblyBeANorthAmericanPhoneNumber: 'Invalid phone number',
              }}
              showErrorAfterBlur
              className="DialerDirectAdd-number"
            />
            <GroupSchoolSelect
              hideUnaffiliated
              filterUnadministered
              initialOrganizationId={CurrentUser.getUser().getOrganizationId()}
              input="select"
              prefix="At "
              onSchoolChange={this.changeSchool}
            />
            <FormSelect
              label="Role"
              name="Role"
              required
              onChange={this.changeRole}
              value={this.state.role}
              validationErrors={{ isDefaultRequiredValue: 'Please select a role' }}
              options={options}
              data-test="role"
            />
            <Text color="thunder" size="xxsmall" align="center">
              {
                'By adding people, I confirm that they gave me permission to send them text messages and emails. '
              }
              <ModalTrigger modal={<DirectAddLegalDialog hasChildren />}>
                <Link>Learn more</Link>
              </ModalTrigger>
            </Text>
            <div className="DialerDirectAdd-buttons">
              <Button
                type="button"
                level="secondary"
                className="DialerDirectAdd-back"
                onClick={this.props.onRequestHide}>
                Back
              </Button>
              <Button
                type="submit"
                level="primary"
                eventElementName={'dialer_direct_add_' + this.state.role}
                className="DialerDirectAdd-submit">
                Add
              </Button>
            </div>
          </Form>
        </BoxBody>
      </Dialog>
    );
  }
}

export default DialerDirectAddModal;
